import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Logo from '@/components/Logo';
import { Mail, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackPageView, trackAddPaymentInfo, trackPurchase } from '../services/facebookTracking';

const API = process.env.REACT_APP_BACKEND_URL;

const SignupPage = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    password: '',
    phone: '',
    business_category: ''
  });

  // Track PageView on mount
  useEffect(() => {
    trackPageView();
  }, []);

  // Vérifier le paiement Stripe au chargement
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      verifyPayment(sessionId);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      // Polling pour vérifier le statut du paiement
      const maxAttempts = 5;
      for (let i = 0; i < maxAttempts; i++) {
        const response = await axios.get(`${API}/api/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentVerified(true);
          
          // Get customer email
          const customerEmail = response.data.customer_email;
          
          // Pré-remplir l'email si disponible
          if (customerEmail) {
            setFormData(prev => ({ ...prev, email: customerEmail }));
          }
          
          // Track Facebook events - AddPaymentInfo and Purchase
          trackAddPaymentInfo(89.0, 'EUR', customerEmail);
          trackPurchase(89.0, 'EUR', sessionId, customerEmail);
          
          toast.success('Paiement confirmé ! Créez votre compte.');
          break;
        } else if (response.data.status === 'expired') {
          toast.error('Session de paiement expirée. Veuillez réessayer.');
          break;
        }
        
        // Attendre avant de réessayer
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Erreur lors de la vérification du paiement');
    } finally {
      setVerifyingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { email, password, ...userData } = formData;
      const result = await signup(email, password, userData);
      
      if (result?.requiresEmailVerification) {
        setEmailSent(true);
        setRegisteredEmail(email);
        toast.success('Un email de confirmation vous a été envoyé!');
      } else {
        toast.success('Compte créé avec succès!');
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.message?.includes('already registered')) {
        toast.error('Cet email est déjà utilisé');
      } else {
        toast.error(error.message || 'Échec de la création du compte');
      }
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-2xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Vérifiez votre email 📧
          </h1>
          
          <p className="text-gray-600 mb-4">
            Nous avons envoyé un email de confirmation à :
          </p>
          
          <p className="font-semibold text-[#2E59D7] text-lg mb-6">
            {registeredEmail}
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3 text-left">
              <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">Que faire maintenant ?</p>
                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                  <li>Ouvrez votre boîte mail</li>
                  <li>Cliquez sur le lien de confirmation</li>
                  <li>Revenez ici pour vous connecter</li>
                </ol>
              </div>
            </div>
          </div>
          
          <Link 
            to="/login" 
            className="text-[#2E59D7] hover:text-[#0061A0] font-semibold text-sm"
          >
            ← Aller à la page de connexion
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        <div className="flex items-center justify-center mb-8">
          <Logo />
        </div>
        
        {/* Message de vérification du paiement */}
        {verifyingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-blue-800">Vérification du paiement en cours...</span>
          </div>
        )}
        
        {/* Message de confirmation du paiement */}
        {paymentVerified && !verifyingPayment && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-green-600" />
            <div>
              <span className="text-green-800 font-semibold">Paiement confirmé !</span>
              <p className="text-green-700 text-sm">Finalisez votre inscription ci-dessous.</p>
            </div>
          </div>
        )}
        
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">Créer un compte</h1>
        <p className="text-center text-gray-600 mb-8">Développez votre entreprise</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="business_name">Nom de l'entreprise</Label>
            <Input
              id="business_name"
              type="text"
              placeholder="Nom de votre entreprise"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="business_category">Catégorie</Label>
            <Select 
              value={formData.business_category} 
              onValueChange={(value) => setFormData({ ...formData, business_category: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="restaurant">Restaurant</SelectItem>
                <SelectItem value="barbershop">Coiffeur</SelectItem>
                <SelectItem value="hotel">Hôtel</SelectItem>
                <SelectItem value="retail">Commerce</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="owner_name">Votre nom</Label>
            <Input
              id="owner_name"
              type="text"
              placeholder="Votre nom"
              value={formData.owner_name}
              onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="vous@entreprise.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phone">Téléphone (Optionnel)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+33 6 12 34 56 78"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]" 
            disabled={loading}
          >
            {loading ? 'Création en cours...' : 'Créer un compte'}
          </Button>
        </form>

        <p className="text-center text-gray-600 mt-6">
          Vous avez déjà un compte ?{' '}
          <Link to="/login" className="text-[#2E59D7] hover:text-[#0061A0] font-semibold">
            Se connecter
          </Link>
        </p>
      </Card>
    </div>
  );
};

export default SignupPage;
